import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Loader from '../../Global/Loader';

const MAX_FILE_SIZE_MB = 2;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const SECTION_CONFIG = [
  {
    title: 'Upload CNIC',
    description: null,
    keys: ['idCardFront', 'idCardBack']
  },
  {
    title: 'Upload English Proficiency Test',
    description: null,
    keys: ['englishProficiencyTest']
  },
  {
    title: 'Upload Educational Documents',
    description:
      'All Documents should be verified from HEC and relevant Department',
    keys: ['schoolTranscript', 'collegeTranscript', 'bachelorTranscript', 'bachelorDegree']
  },
  {
    title: 'Additional Documents',
    description: null,
    keys: ['letterOfRecommendation1', 'letterOfRecommendation2', 'mediumOfInstruction']
  }
];

const StudentDocuments = () => {
  const [documents, setDocuments] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [uploading, setUploading] = useState({});
  const [removing, setRemoving] = useState({});
  const [previewUrls, setPreviewUrls] = useState({});
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem('token')}`
    }),
    []
  );

  const resetNotices = () => {
    setMessage(null);
    setError(null);
  };

  const loadPreviewUrls = async (docMap) => {
    const entries = await Promise.all(
      Object.entries(docMap).map(async ([key, meta]) => {
        if (!meta?.uploaded) {
          return [key, null];
        }

        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BASE_URL}/student/documents/${key}/url`,
            { headers }
          );
          const url = response.data?.data?.signedUrl || null;
          return [key, url];
        } catch (err) {
          console.error('Failed to load preview for', key, err);
          return [key, null];
        }
      })
    );

    setPreviewUrls((prev) => {
      const next = { ...prev };
      entries.forEach(([key, url]) => {
        if (url) {
          next[key] = url;
        } else {
          delete next[key];
        }
      });
      return next;
    });
  };

  const fetchDocuments = async () => {
    try {
      setIsLoading(true);
      resetNotices();
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/student/documents`,
        { headers }
      );

      const docsArray = response.data?.data?.documents || [];
      const docMap = docsArray.reduce((acc, doc) => {
        acc[doc.key] = doc;
        return acc;
      }, {});
      setDocuments(docMap);
      await loadPreviewUrls(docMap);
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to load documents.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileChange = async (type, file, resetInput) => {
    if (!file) return;
    resetNotices();

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`File size exceeds ${MAX_FILE_SIZE_MB} MB. Please upload a smaller file.`);
      resetInput();
      return;
    }

    const formData = new FormData();
    formData.append('type', type);
    formData.append('document', file);

    try {
      setUploading((prev) => ({ ...prev, [type]: true }));
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/student/documents`,
        formData,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setMessage('Document uploaded successfully.');
      await fetchDocuments();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to upload document.');
    } finally {
      setUploading((prev) => ({ ...prev, [type]: false }));
      resetInput();
    }
  };

  const handleViewDocument = async (type) => {
    resetNotices();
    try {
      const existing = previewUrls[type];
      if (existing) {
        window.open(existing, '_blank', 'noopener');
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/student/documents/${type}/url`,
        { headers }
      );
      const signedUrl = response.data?.data?.signedUrl;
      if (signedUrl) {
        setPreviewUrls((prev) => ({ ...prev, [type]: signedUrl }));
        window.open(signedUrl, '_blank', 'noopener');
      } else {
        setError('No file available to view.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to retrieve document.');
    }
  };

  const handleRemoveDocument = async (type) => {
    resetNotices();
    try {
      setRemoving((prev) => ({ ...prev, [type]: true }));
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/student/documents/${type}`,
        { headers }
      );
      setMessage('Document removed successfully.');
      await fetchDocuments();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to remove document.');
    } finally {
      setRemoving((prev) => ({ ...prev, [type]: false }));
    }
  };

  const isPdfDocument = (name = '') => name.toLowerCase().endsWith('.pdf');

  const renderDocumentField = (docKey) => {
    const docMeta = documents[docKey] || {};
    const inputId = `doc-${docKey}`;
    const previewUrl = previewUrls[docKey];
    const isPdf = isPdfDocument(docMeta.originalName || '');

    return (
      <div key={docKey} className="relative ">
        <label htmlFor={inputId} className="text-sm text-gray-500 top-[-12px] left-3 bg-white px-1 absolute">
          {docMeta.label || docKey.replace(/([A-Z])/g, ' $1')}
        </label>
        <input
          id={inputId}
          name={docKey}
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          className="block w-full p-2 border rounded border-[#6b7280] cursor-pointer bg-gray-50 focus:outline-none"
          onChange={(event) => {
            const inputEl = event.target;
            const file = inputEl.files?.[0] || null;
            handleFileChange(docKey, file, () => {
              if (inputEl) {
                inputEl.value = '';
              }
            });
          }}
          disabled={uploading[docKey]}
        />
        <p className="text-gray-500 text-xs mt-1">
          {docMeta.description || 'Upload a JPG, PNG, or PDF document (max 2 MB)'}
        </p>
        {docMeta.uploaded && (
          <div className="mt-2 text-xs text-gray-600">
            <p className="font-semibold">Uploaded File</p>
            <p>{docMeta.originalName || 'Document available'}</p>
            {docMeta.uploadedAt && (
              <p className="text-[10px] text-gray-400">
                Uploaded on {new Date(docMeta.uploadedAt).toLocaleString()}
              </p>
            )}
            {previewUrl ? (
              <div className="mt-3">
                {isPdf ? (
                  <iframe
                    title={`${docMeta.label || docKey} preview`}
                    src={previewUrl}
                    className="w-full h-40 border rounded"
                  />
                ) : (
                  <img
                    src={previewUrl}
                    alt={`${docMeta.label || docKey} preview`}
                    className="w-full h-40 object-cover border rounded"
                  />
                )}
              </div>
            ) : (
              <p className="text-gray-400 text-[11px] mt-2">
                Preview unavailable. Use the view option to open the file.
              </p>
            )}
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => handleViewDocument(docKey)}
                className="px-3 py-1.5 rounded border border-blue-500 text-blue-500 text-xs font-medium hover:bg-blue-500 hover:text-white transition"
              >
                View
              </button>
              <button
                type="button"
                onClick={() => handleRemoveDocument(docKey)}
                className="px-3 py-1.5 rounded border border-red-500 text-red-500 text-xs font-medium hover:bg-red-500 hover:text-white transition"
                disabled={removing[docKey]}
              >
                {removing[docKey] ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        )}
        {uploading[docKey] && !docMeta.uploaded && (
          <p className="text-blue-500 text-xs mt-2">Uploading...</p>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col space-y-6 w-[72%]">
    <div className="px-10 py-6 bg-white rounded shadow-lg">
      <h2 className="text-xl font-semibold text-gray-800 mb-4">Documents</h2>
      <p className="text-sm text-gray-500 mb-6">
        Upload and manage your documents at any time. All documents are optional and can be updated later.
      </p>

      {isLoading && (
        <div className="flex justify-center mb-6">
          <Loader />
        </div>
      )}

      {(message || error) && (
        <div
          className={`mb-6 px-4 py-3 rounded border ${
            message
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          {message || error}
        </div>
      )}

      {SECTION_CONFIG.map((section) => (
        <div key={section.title} className="mb-6">
          <h2 className="text-gray-800 text-lg font-semibold mt-6 mb-4">
            {section.title}
          </h2>

          {section.description && (
            <div className="flex items-start bg-blue-100 border border-blue-300 text-blue-700 px-4 py-3 rounded mt-4 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 flex-shrink-0 mr-3"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M12 20h.01M12 20h-2a2 2 0 01-2-2V6a2 2 0 012-2h4a2 2 0 012 2v12a2 2 0 01-2 2h-2zm0-18a2 2 0 00-2-2v.01a2 2 0 002 2zm0 4a2 2 0 00-2-2v.01a2 2 0 002 2z"
                />
              </svg>
              <div>
                <span className="text-sm">{section.description}</span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {section.keys.map((key) => renderDocumentField(key))}
          </div>
        </div>
      ))}
    </div>
    </div>
  );
};

export default StudentDocuments;
